import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, IconButton, TextField, Button } from '@mui/material';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';

const url = process.env.REACT_APP_SERVER_URL;

const CallLogsGraph = ({ callLogs, showCallLogGraph, setShowCallLogGraph }) => {
  const [heatMapData, setHeatMapData] = useState([]);
  
  // Initialize with current month
  const now = new Date();
  const [startDate, setStartDate] = useState(format(startOfMonth(now), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(now), 'yyyy-MM-dd'));

  useEffect(() => {
    formatCallLogData();
  }, [callLogs]);

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const timeSlots = ['Morning\n(6am-12pm)', 'Afternoon\n(12pm-6pm)', 'Evening\n(6pm-12am)', 'Night\n(12am-6am)'];

  const formatCallLogData = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Initialize grid: 7 days × 4 time slots
    const grid = Array.from({ length: 7 }, (_, dayIndex) => ({
      day: daysOfWeek[dayIndex],
      slots: Array.from({ length: 4 }, () => ({
        count: 0,
        calls: [] // Store individual call details for hover
      }))
    }));

    // Process each call
    for (const call of callLogs) {
      const dateObj = new Date(call.callDate);
      if (dateObj < start || dateObj > end) continue;

      const dayOfWeek = dateObj.getDay(); // 0-6 (Sun-Sat)
      const hour = dateObj.getHours(); // 0-23

      // Determine time slot: 0=Morning(6-12), 1=Afternoon(12-18), 2=Evening(18-24), 3=Night(0-6)
      let slotIndex;
      if (hour >= 6 && hour < 12) slotIndex = 0;
      else if (hour >= 12 && hour < 18) slotIndex = 1;
      else if (hour >= 18 && hour < 24) slotIndex = 2;
      else slotIndex = 3;

      grid[dayOfWeek].slots[slotIndex].count++;
      grid[dayOfWeek].slots[slotIndex].calls.push(call);
    }

    setHeatMapData(grid);
  };

  function handleCloseCallLogGraph() {
    setShowCallLogGraph(false);
  }

  function handleSearch() {
    formatCallLogData();
  }

  const renderDots = (count) => {
    if (count === 0) return null;
    
    const dots = [];
    for (let i = 0; i < count; i++) {
      // Random positioning within cell for organic clustering
      const x = Math.random() * 80 + 10; // 10-90% of cell width
      const y = Math.random() * 80 + 10; // 10-90% of cell height
      
      dots.push(
        <circle
          key={i}
          cx={`${x}%`}
          cy={`${y}%`}
          r="3"
          fill="#157a6e"
          opacity="0.7"
        />
      );
    }
    return dots;
  };

  const cellWidth = 180;
  const cellHeight = 45;

  return (
    <Dialog open={showCallLogGraph} onClose={handleCloseCallLogGraph} maxWidth="lg" fullWidth>
      <DialogTitle>
        Call Logs 
        <IconButton
          onClick={handleCloseCallLogGraph}
          style={{ position: 'absolute', right: 8, top: 8 }}
        >
          ✕
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px' }}>
          
          {/* Left side - Graph */}
          <div style={{ flex: 1, overflowX: 'auto' }}>
            <svg width={timeSlots.length * cellWidth + 80} height={daysOfWeek.length * cellHeight + 60}>
              {/* Y-axis labels (Days) */}
              {daysOfWeek.map((day, dayIndex) => (
                <text
                  key={day}
                  x="40"
                  y={dayIndex * cellHeight + cellHeight / 2 + 50}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize="14"
                  fontWeight="bold"
                >
                  {day}
                </text>
              ))}

              {/* X-axis labels (Time slots) */}
              {timeSlots.map((slot, slotIndex) => (
                <text
                  key={slot}
                  x={slotIndex * cellWidth + cellWidth / 2 + 60}
                  y="30"
                  textAnchor="middle"
                  fontSize="12"
                >
                  {slot.split('\n').map((line, i) => (
                    <tspan key={i} x={slotIndex * cellWidth + cellWidth / 2 + 60} dy={i === 0 ? 0 : 14}>
                      {line}
                    </tspan>
                  ))}
                </text>
              ))}

              {/* Grid cells with dots */}
              {heatMapData.map((dayData, dayIndex) =>
                dayData.slots.map((slotData, slotIndex) => {
                  const x = slotIndex * cellWidth + 60;
                  const y = dayIndex * cellHeight + 50;

                  return (
                    <g key={`${dayIndex}-${slotIndex}`}>
                      {/* Cell background */}
                      <rect
                        x={x}
                        y={y}
                        width={cellWidth - 2}
                        height={cellHeight - 2}
                        fill="#f9f9f9"
                        stroke="#ddd"
                        strokeWidth="1"
                      />
                      
                      {/* Dots */}
                      <svg x={x} y={y} width={cellWidth - 2} height={cellHeight - 2}>
                        {renderDots(slotData.count)}
                      </svg>

                      {/* Count badge in top-right corner */}
                      {slotData.count > 0 && (
                        <text
                          x={x + cellWidth - 12}
                          y={y + 12}
                          fontSize="11"
                          fontWeight="bold"
                          fill="#157a6e"
                          textAnchor="end"
                        >
                          {slotData.count}
                        </text>
                      )}
                    </g>
                  );
                })
              )}
            </svg>
          </div>

          {/* Right side - Date Range Filter */}
          <div style={{ 
            minWidth: '200px', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '16px',
            paddingTop: '20px'
          }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              fullWidth
            >
              Search
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CallLogsGraph;